import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "./PageHeader";
import { EntityForm } from "./EntityForm";

export default function CrudPage({ title, subtitle, endpoint, query = "", fields, columns, defaults, searchKeys = [], prefix, extraTop, onChanged, itemLabel = "Data", embedded = false, rowActions }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [delItem, setDelItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/${endpoint}${query ? `?${query}` : ""}`);
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, query]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = () => {
    load();
    onChanged?.();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...defaults });
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...defaults, ...item });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0);
    });
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(`${itemLabel} berhasil ${editing ? "diperbarui" : "ditambahkan"}`);
      setOpen(false);
      changed();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/${endpoint}/${delItem.id}`);
      toast.success(`${itemLabel} dihapus`);
      setDelItem(null);
      changed();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const ql = q.toLowerCase();
  const filtered = ql ? items.filter((i) => searchKeys.some((k) => String(i[k] ?? "").toLowerCase().includes(ql))) : items;

  return (
    <div data-testid={`${prefix}-page`}>
      {embedded ? (
        <div className="flex justify-end mb-4">
          <Button onClick={openCreate} className="rounded-full emerald-bg hover:bg-[#145c48]" data-testid={`${prefix}-add-btn`}>
            <Plus size={16} className="mr-1" /> Tambah
          </Button>
        </div>
      ) : (
        <PageHeader title={title} subtitle={subtitle}>
          <Button onClick={openCreate} className="rounded-full emerald-bg hover:bg-[#145c48]" data-testid={`${prefix}-add-btn`}>
            <Plus size={16} className="mr-1" /> Tambah
          </Button>
        </PageHeader>
      )}
      {extraTop}
      <div className="rounded-3xl bg-white border border-emerald-900/10 overflow-hidden">
        <div className="p-4 border-b border-emerald-900/10 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className="pl-9 rounded-full" data-testid={`${prefix}-search-input`} />
          </div>
          <span className="text-sm text-slate-500" data-testid={`${prefix}-count`}>{filtered.length} data</span>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid={`${prefix}-table`}>
            <TableHeader>
              <TableRow>
                {columns.map((c) => <TableHead key={c.key} className={c.className}>{c.label}</TableHead>)}
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} />Memuat...</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-slate-400" data-testid={`${prefix}-empty`}>Belum ada data.</TableCell></TableRow>
              )}
              {filtered.map((item) => (
                <TableRow key={item.id} data-testid={`${prefix}-row-${item.id}`}>
                  {columns.map((c) => <TableCell key={c.key} className={c.className}>{c.render ? c.render(item) : item[c.key]}</TableCell>)}
                  <TableCell className="text-right whitespace-nowrap">
                    {rowActions?.(item)}
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`${prefix}-edit-${item.id}`}><Pencil size={15} /></Button>
                    <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" onClick={() => setDelItem(item)} data-testid={`${prefix}-delete-${item.id}`}><Trash2 size={15} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${prefix}-dialog`}>
          <DialogHeader><DialogTitle>{editing ? `Edit ${itemLabel}` : `Tambah ${itemLabel}`}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-5">
            <EntityForm fields={fields} form={form} setForm={setForm} prefix={prefix} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid={`${prefix}-cancel-btn`}>Batal</Button>
              <Button type="submit" disabled={saving} className="rounded-full emerald-bg hover:bg-[#145c48]" data-testid={`${prefix}-save-btn`}>
                {saving ? <Loader2 className="animate-spin" size={16} /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delItem} onOpenChange={(v) => !v && setDelItem(null)}>
        <AlertDialogContent data-testid={`${prefix}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {itemLabel}?</AlertDialogTitle>
            <AlertDialogDescription>Data ini akan dihapus permanen dan tidak dapat dikembalikan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${prefix}-delete-cancel`}>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid={`${prefix}-delete-confirm`}>Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
